import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { FileText, Globe, Clock, AlertTriangle, Loader2, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';

const DPE_CONFIG = {
  A: { color: '#22c55e' },
  B: { color: '#84cc16' },
  C: { color: '#eab308' },
  D: { color: '#f97316' },
  E: { color: '#ef4444' },
  F: { color: '#dc2626' },
  G: { color: '#991b1b' },
};

const getRiskFromScore = (score) => {
  if (score >= 80) return { label: 'EXCELLENT', color: 'text-green-500', bg: 'bg-green-500/10' };
  if (score >= 60) return { label: 'MOYEN',     color: 'text-yellow-500', bg: 'bg-yellow-500/10' };
  if (score >= 40) return { label: 'ÉLEVÉ',     color: 'text-orange-500', bg: 'bg-orange-500/10' };
  return              { label: 'CRITIQUE',   color: 'text-red-500',    bg: 'bg-red-500/10' };
};

export default function Reports() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const plan = user?.subscription_plan || 'free';
  const canAccess = plan === 'pro' || plan === 'enterprise';

  useEffect(() => {
    if (!token) return;
    fetchReports();
  }, [token]);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(res.data);
    } catch (err) {
      setError("Impossible de charger les rapports.");
    } finally {
      setLoading(false);
    }
  };

  if (!canAccess) return (
    <DashboardLayout>
      <div className="max-w-md mx-auto mt-20 p-8 border border-zinc-800 bg-zinc-900 text-center space-y-4">
        <FileText className="w-12 h-12 text-zinc-600 mx-auto" />
        <h2 className="text-white font-mono font-bold text-sm uppercase">Rapports IA — Plan Pro</h2>
        <p className="text-zinc-500 font-mono text-xs leading-relaxed">
          Les rapports IA sont disponibles avec le plan Pro ou Enterprise.
        </p>
        <button
          onClick={() => navigate('/billing')}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-mono text-xs uppercase py-3 transition-all"
        >
          Voir les plans →
        </button>
      </div>
    </DashboardLayout>
  );

  if (loading) return (
    <DashboardLayout>
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        <p className="font-mono text-xs text-zinc-500 uppercase tracking-widest animate-pulse">Chargement des rapports...</p>
      </div>
    </DashboardLayout>
  );

  if (error) return (
    <DashboardLayout>
      <div className="max-w-md mx-auto mt-20 p-8 border border-red-900/30 bg-red-500/5 text-center space-y-4">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto" />
        <p className="text-zinc-400 font-mono text-sm">{error}</p>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-black font-mono uppercase text-white tracking-tight">Rapports IA</h1>
            <p className="text-zinc-500 font-mono text-xs mt-1">{reports.length} rapport{reports.length > 1 ? 's' : ''} généré{reports.length > 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => navigate('/audits/new')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-mono text-xs uppercase rounded-sm transition-all"
          >
            <Plus className="w-3 h-3" /> Nouvel audit
          </button>
        </div>

        {/* Liste des rapports */}
        {reports.length === 0 ? (
          <div className="text-center py-20 border border-zinc-800 rounded-sm bg-zinc-900">
            <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 font-mono text-sm mb-2">Aucun rapport généré</p>
            <p className="text-zinc-600 font-mono text-xs mb-6">Lancez un audit puis générez votre premier rapport IA</p>
            <button
              onClick={() => navigate('/audits/new')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-mono text-xs uppercase px-6 py-3 transition-all"
            >
              Lancer un audit →
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => {
              const risk = getRiskFromScore(report.security_score);
              const dpe = DPE_CONFIG[report.dpe_grade] || DPE_CONFIG['D'];
              return (
                <div
                  key={report.report_id}
                  onClick={() => navigate(`/reports/${report.report_id}`)}
                  className="bg-zinc-900 border border-zinc-800 hover:border-blue-500/30 rounded-sm p-5 cursor-pointer transition-all group"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">

                      {/* Grade DPE */}
                      {report.dpe_grade && (
                        <div
                          className="w-10 h-10 flex items-center justify-center rounded-sm border-2 shrink-0 font-black font-mono text-lg text-white"
                          style={{ borderColor: dpe.color, backgroundColor: dpe.color + '30' }}
                        >
                          <span style={{ color: dpe.color }}>{report.dpe_grade}</span>
                        </div>
                      )}

                      <div className="min-w-0">
                        <h3 className="text-white font-mono font-bold text-sm truncate group-hover:text-blue-400 transition-colors">
                          {report.audit_name || report.target_domain}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-zinc-500 font-mono text-[10px]">
                          <span className="flex items-center gap-1">
                            <Globe className="w-3 h-3" /> {report.target_domain}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {new Date(report.created_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 shrink-0">
                      {/* Score */}
                      <div className="text-right">
                        <div className={`text-xl font-black font-mono ${risk.color}`}>
                          {report.security_score}%
                        </div>
                        <div className={`text-[9px] font-mono uppercase px-2 py-0.5 rounded-sm ${risk.color} ${risk.bg}`}>
                          {risk.label}
                        </div>
                      </div>

                      {/* Flèche */}
                      <div className="text-zinc-600 group-hover:text-blue-400 transition-colors font-mono text-xs">
                        →
                      </div>
                    </div>
                  </div>

                  {/* Résumé */}
                  {report.summary && (
                    <p className="text-zinc-600 font-mono text-[10px] mt-3 truncate">
                      {report.summary}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
